import type { Policy, PtoBalance, PtoRequest, TimeOffEntry } from '../types/hr';

export const policies: Policy[] = [
  {
    id: 'newhire',
    name: 'New Hire PTO',
    tagline: 'First-year accrual with a 30-day waiting period and an anniversary top-up.',
    type: 'Accrual',
    updated: '2026-10-14',
    hasTbd: false,
    overview:
      'Applies to every new full-time hire during their first year. PTO begins accruing after a 30-day waiting period, is prorated by start date, and caps at 12 days. At the first work anniversary the balance is topped up to 15 days and the employee moves to Regular PTO.',
    highlights: [
      { label: 'Accrual', value: '1 day / month' },
      { label: 'Year-one cap', value: '12 days' },
      { label: 'Anniversary', value: 'Top up to 15' },
    ],
    sections: [
      {
        id: 'eligibility',
        title: 'Eligibility & waiting period',
        rules: [
          { label: 'Eligible employees', value: 'All new full-time hires, from hire date to first work anniversary' },
          { label: 'Waiting period', value: '30 calendar days from hire date' },
          { label: 'Accrual begins', value: 'Day 31 of employment' },
          { label: 'During waiting period', value: 'Requests allowed; unpaid unless approved as an advance' },
        ],
      },
      {
        id: 'accrual',
        title: 'Accrual',
        rules: [
          { label: 'Accrual rate', value: '1 day per month' },
          { label: 'Frequency', value: 'Monthly, posted on the 1st' },
          { label: 'Proration', value: 'Yes — first partial month prorated by days employed after waiting period' },
          { label: 'Accrual pauses', value: 'When the balance reaches 12 days' },
        ],
      },
      {
        id: 'caps',
        title: 'Caps',
        rules: [
          { label: 'Year-one maximum', value: '12 days' },
          { label: 'Negative balance', value: 'Not allowed' },
        ],
      },
      {
        id: 'anniversary',
        title: 'Anniversary rule',
        rules: [
          { label: 'Trigger', value: 'First work anniversary' },
          { label: 'Rule', value: 'Top balance UP TO 15 days (not +15)', note: 'If the balance is already 15 or more, nothing is added.' },
          { label: 'After anniversary', value: 'Employee transitions to Regular PTO' },
        ],
      },
      {
        id: 'carryover',
        title: 'Carryover',
        rules: [
          { label: 'Carryover', value: 'Full balance carries into Regular PTO at the anniversary' },
          { label: 'Year-end', value: 'No forfeiture during year one' },
        ],
      },
    ],
    example: {
      before: '10 days banked',
      added: '+5 days',
      after: '15 days',
      caption:
        'The top-up brings the balance to 15 — it never adds a flat 15. An employee with 14 days receives +1; an employee with 15 or more receives nothing.',
    },
    form: {
      name: 'New Hire PTO',
      type: 'Accrual',
      accrualRate: '1',
      accrualFrequency: 'Monthly',
      waitingDays: '30',
      prorate: true,
      yearOneCap: '12',
      anniversaryMode: 'top-up',
      anniversaryAmount: '15',
      carryover: '15',
      departments: ['Operations', 'Underwriting', 'Processing', 'Closing', 'Sales', 'Marketing', 'IT', 'Finance', 'HR'],
    },
  },
  {
    id: 'regular',
    name: 'Regular PTO',
    tagline: 'Tenure-based yearly accrual for operations staff: 12 → 15 → 17 → 20 days.',
    type: 'Accrual',
    updated: '2026-11-04',
    hasTbd: false,
    overview:
      'Ongoing paid time off for operations staff. Days are granted once a year on January 1st, and the yearly amount steps up with tenure: 12 days to start, 15 after 1 year, 17 after 4 years and 20 after 7 years of service. There is no balance cap, and up to 5 unused days carry over into the next year.',
    highlights: [
      { label: 'Starting accrual', value: '12 days / yr' },
      { label: 'Top tier', value: '20 days at 7 yrs' },
      { label: 'Carryover', value: 'Up to 5 days' },
    ],
    sections: [
      {
        id: 'eligibility',
        title: 'Eligibility & waiting period',
        rules: [
          { label: 'Eligible employees', value: 'Operations staff (pilot group of 15)' },
          { label: 'Waiting period', value: 'None — accrual begins immediately on hire' },
        ],
      },
      {
        id: 'accrual',
        title: 'Accrual',
        rules: [
          { label: 'Method', value: 'Tenure-based schedule · 4 tiers' },
          { label: 'Starting accrual', value: '12 days / year' },
          { label: 'Posted', value: 'Jan 1st each year' },
          { label: 'Tier applied', value: 'Based on completed years of service on the posting date' },
        ],
      },
      {
        id: 'caps',
        title: 'Caps',
        rules: [{ label: 'Balance cap', value: 'No cap — employees keep accruing regardless of balance' }],
      },
      {
        id: 'anniversary',
        title: 'Anniversary rule',
        rules: [{ label: 'Milestones', value: 'Accrual steps up at 1, 4 and 7 years of service' }],
      },
      {
        id: 'carryover',
        title: 'Carryover',
        rules: [
          { label: 'Carryover limit', value: 'Up to 5 days' },
          { label: 'Carryover date', value: 'December 31', note: 'Unused days above 5 are forfeited at year end.' },
        ],
      },
    ],
    form: {
      name: 'Regular PTO',
      type: 'Accrual',
      accrualRate: '12',
      accrualFrequency: 'Annually',
      waitingDays: '0',
      prorate: false,
      yearOneCap: '',
      anniversaryMode: 'add',
      anniversaryAmount: '',
      carryover: '5',
      departments: ['Operations'],
      schedule: [
        { id: 'tier-start', afterYears: '0', days: '12', frequency: 'Yearly', postDay: '1', postMonth: 'January', capEnabled: false, cap: '', carryoverEnabled: true, carryover: '5' },
        { id: 'tier-1', afterYears: '1', days: '15', frequency: 'Yearly', postDay: '1', postMonth: 'January', capEnabled: false, cap: '', carryoverEnabled: true, carryover: '5' },
        { id: 'tier-4', afterYears: '4', days: '17', frequency: 'Yearly', postDay: '1', postMonth: 'January', capEnabled: false, cap: '', carryoverEnabled: true, carryover: '5' },
        { id: 'tier-7', afterYears: '7', days: '20', frequency: 'Yearly', postDay: '1', postMonth: 'January', capEnabled: false, cap: '', carryoverEnabled: true, carryover: '5' },
      ],
    },
  },
];

// Approved time off. Regular PTO entries belong to the Operations pilot group.
export const timeOffEntries: TimeOffEntry[] = [
  { id: 't1', employeeId: 'e25', start: '2026-11-03', end: '2026-11-05', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't2', employeeId: 'e26', start: '2026-11-04', end: '2026-11-04', days: 1, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't3', employeeId: 'e27', start: '2026-11-04', end: '2026-11-06', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't4', employeeId: 'e28', start: '2026-11-02', end: '2026-11-06', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't5', employeeId: 'e29', start: '2026-11-04', end: '2026-11-04', days: 0.5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't6', employeeId: 'e21', start: '2026-10-01', end: '2026-12-31', days: 66, policyLabel: 'Leave of Absence', tone: 'teal' },
  { id: 't7', employeeId: 'e30', start: '2026-11-06', end: '2026-11-06', days: 1, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't8', employeeId: 'e31', start: '2026-11-05', end: '2026-11-06', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't9', employeeId: 'e32', start: '2026-11-12', end: '2026-11-13', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't10', employeeId: 'e33', start: '2026-11-16', end: '2026-11-20', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't11', employeeId: 'e34', start: '2026-11-18', end: '2026-11-19', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't12', employeeId: 'e35', start: '2026-11-23', end: '2026-11-24', days: 2, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't13', employeeId: 'e36', start: '2026-11-25', end: '2026-11-27', days: 3, policyLabel: 'Regular PTO', tone: 'blue' },
  { id: 't14', employeeId: 'e37', start: '2026-12-14', end: '2026-12-18', days: 5, policyLabel: 'Regular PTO', tone: 'blue' },
];

export const ptoRequests: PtoRequest[] = [
  { id: 'r1', employeeId: 'e38', start: '2026-11-24', end: '2026-11-26', days: 2.5, policyId: 'regular', note: 'Thanksgiving travel — back online Monday.', submitted: '2026-11-01', balanceBefore: 8, status: 'pending' },
  { id: 'r2', employeeId: 'e39', start: '2026-12-21', end: '2026-12-31', days: 7, policyId: 'regular', note: 'Holiday trip to visit family.', submitted: '2026-11-02', balanceBefore: 11.5, status: 'pending' },
  { id: 'r3', employeeId: 'e31', start: '2026-11-20', end: '2026-11-20', days: 1, policyId: 'regular', note: 'Medical appointment.', submitted: '2026-11-03', balanceBefore: 8, status: 'pending' },
  { id: 'r4', employeeId: 'e33', start: '2026-12-07', end: '2026-12-08', days: 2, policyId: 'regular', note: 'Moving apartments.', submitted: '2026-11-03', balanceBefore: 9.5, status: 'pending' },
  { id: 'r5', employeeId: 'e8', start: '2026-12-24', end: '2026-12-24', days: 1, policyId: 'newhire', note: 'Holiday — I understand I may still be in my waiting period.', submitted: '2026-11-04', balanceBefore: 0, status: 'pending' },
];

export const balances: PtoBalance[] = [
  { employeeId: 'e25', policyId: 'regular', available: 9.5 },
  { employeeId: 'e26', policyId: 'regular', available: 8 },
  { employeeId: 'e27', policyId: 'regular', available: 10 },
  { employeeId: 'e28', policyId: 'regular', available: 7.5 },
  { employeeId: 'e29', policyId: 'regular', available: 8.5 },
  { employeeId: 'e30', policyId: 'regular', available: 11 },
  { employeeId: 'e31', policyId: 'regular', available: 8 },
  { employeeId: 'e32', policyId: 'regular', available: 9 },
  { employeeId: 'e33', policyId: 'regular', available: 9.5 },
  { employeeId: 'e34', policyId: 'regular', available: 12 },
  { employeeId: 'e35', policyId: 'regular', available: 7.5 },
  { employeeId: 'e36', policyId: 'regular', available: 10.5 },
  { employeeId: 'e37', policyId: 'regular', available: 9 },
  { employeeId: 'e38', policyId: 'regular', available: 8 },
  { employeeId: 'e39', policyId: 'regular', available: 11.5 },
  { employeeId: 'e8', policyId: 'newhire', available: 0 },
  { employeeId: 'e13', policyId: 'newhire', available: 0 },
  { employeeId: 'e16', policyId: 'newhire', available: 0 },
  { employeeId: 'e23', policyId: 'newhire', available: 0 },
];

/** Sample Regular PTO days taken in 2026 (fictional). The summer vacation absorbs the remainder so balances reconcile. */
export const regularTimeTaken: { date: string; description: string; days: number }[] = [
  { date: '2026-02-16', description: "Time off — Presidents' Day bridge", days: 1 },
  { date: '2026-05-22', description: 'Time off — Personal day', days: 1 },
  { date: '2026-10-12', description: 'Time off — Columbus Day bridge', days: 1 },
];
